import React, { useEffect, useState } from 'react';
import Sidebar from '@/components/Sidebar';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { UserPlus, Eye, RefreshCw, Calendar, Send, Trash2, Clock, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '@/utils/api';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const TIME_SLOTS = [
  "9:00 AM - 11:00 AM",
  "11:00 AM - 1:00 PM",
  "1:00 PM - 3:00 PM",
  "3:00 PM - 5:00 PM"
];

export default function NewFamilies() {
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [familyToDelete, setFamilyToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [familyToSchedule, setFamilyToSchedule] = useState(null);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [scheduling, setScheduling] = useState(false);
  const [resendingWelcome, setResendingWelcome] = useState(null);
  const [resendingInvite, setResendingInvite] = useState(null);

  useEffect(() => {
    fetchNewFamilies();
  }, []);

  const fetchNewFamilies = async () => {
    setLoading(true);
    try {
      // Get families who have received scheduling invitation but haven't scheduled yet
      const response = await api.get('/families/staged');
      // Filter to show those who received scheduling_invited but delivery not yet confirmed
      const newFamilies = response.data.filter(f => 
        f.scheduling_invited && !f.delivery_confirmed
      );
      setFamilies(newFamilies);
    } catch (error) {
      toast.error('Failed to load families');
    } finally {
      setLoading(false);
    }
  };

  const viewDetails = (family) => {
    setSelectedFamily(family);
    setShowDetailModal(true);
  };

  const confirmDelete = (family) => {
    setFamilyToDelete(family);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!familyToDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/families/${familyToDelete.id}`);
      toast.success(`${familyToDelete.parent_first_name} ${familyToDelete.parent_last_name} has been removed.`);
      setFamilies(prev => prev.filter(f => f.id !== familyToDelete.id));
      setShowDeleteModal(false);
      setFamilyToDelete(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete family');
    } finally {
      setDeleting(false);
    }
  };

  const openScheduleModal = (family) => {
    setFamilyToSchedule(family);
    setScheduleDate('');
    setScheduleTime('');
    setShowScheduleModal(true);
  };

  const handleManualSchedule = async () => {
    if (!familyToSchedule || !scheduleDate || !scheduleTime) {
      toast.error('Please select both a date and time slot');
      return;
    }
    setScheduling(true);
    try {
      const response = await api.post('/admin/manual-schedule-delivery', {
        family_id: familyToSchedule.id,
        delivery_date: scheduleDate,
        delivery_time: scheduleTime
      });
      toast.success(response.data.message);
      setFamilies(prev => prev.filter(f => f.id !== familyToSchedule.id));
      setShowScheduleModal(false);
      setFamilyToSchedule(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to schedule delivery');
    } finally {
      setScheduling(false);
    }
  };

  // Get minimum date (today + 1 day)
  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  const resendWelcomeEmail = async (family) => {
    setResendingWelcome(family.id);
    try {
      const response = await api.post(`/admin/resend-welcome-email/${family.id}`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend welcome email');
    } finally {
      setResendingWelcome(null);
    }
  };

  const resendSchedulingInvitation = async (family) => {
    setResendingInvite(family.id);
    try {
      const response = await api.post(`/admin/resend-scheduling-invitation/${family.id}`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend scheduling invitation');
    } finally {
      setResendingInvite(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      <div className="flex-1 p-8 ml-64">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground" data-testid="page-title">New Families</h1>
              <p className="text-stone-600 mt-1">Families who have received their scheduling invitation and are ready to schedule</p>
            </div>
            <Button variant="outline" onClick={fetchNewFamilies} disabled={loading} className="rounded-full">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {/* Info Card */}
          <Card className="border-green-200 bg-green-50 mb-6">
            <CardContent className="p-4">
              <p className="text-green-800 text-sm">
                <strong>Workflow:</strong> These families have received their scheduling invitation and can now schedule their delivery.
                You can also manually schedule a delivery for families who may have technical issues. Once confirmed, they will move to "All Families".
              </p>
            </CardContent>
          </Card>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto"></div>
              <p className="mt-4 text-stone-600">Loading families...</p>
            </div>
          ) : families.length === 0 ? (
            <Card className="border-stone-200 shadow-card">
              <CardContent className="py-12 text-center">
                <UserPlus className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-stone-600">No New Families</h3>
                <p className="text-stone-500 mt-1">Families will appear here after receiving their scheduling invitation.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {families.map((family) => (
                <Card key={family.id} className="border-stone-200 shadow-card hover:shadow-floating transition-all" data-testid={`new-family-${family.id}`}>
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-semibold text-foreground">
                            {family.parent_first_name} {family.parent_last_name}
                          </h3>
                          {family.has_scheduled_delivery ? (
                            <Badge className="bg-amber-100 text-amber-800" data-testid={`status-badge-${family.id}`}>
                              <Clock className="w-3 h-3 mr-1" />
                              Pending Confirmation
                            </Badge>
                          ) : (
                            <Badge className="bg-blue-100 text-blue-800" data-testid={`status-badge-${family.id}`}>
                              <Send className="w-3 h-3 mr-1" />
                              Invitation Sent
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-stone-600 space-y-1">
                          <p>{family.email} • {family.phone}</p>
                          <p>{family.location_type}: {family.location_detail}</p>
                          <p>{family.children?.length || 0} child{(family.children?.length || 0) !== 1 ? 'ren' : ''}</p>
                          <p className="text-xs text-stone-400">Registered: {formatDate(family.created_at)}</p>
                          {family.scheduling_invited_at && (
                            <p className="text-xs text-blue-600">Scheduling invite sent: {formatDate(family.scheduling_invited_at)}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2">
                        <Button
                          size="sm"
                          onClick={() => openScheduleModal(family)}
                          data-testid={`schedule-${family.id}`}
                          className="rounded-full"
                        >
                          <Calendar className="w-4 h-4 mr-1" />
                          Schedule Delivery
                        </Button>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => resendWelcomeEmail(family)}
                            disabled={resendingWelcome === family.id}
                            data-testid={`resend-welcome-${family.id}`}
                            className="rounded-full text-xs"
                          >
                            <Mail className="w-3 h-3 mr-1" />
                            {resendingWelcome === family.id ? '...' : 'Resend Welcome'}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => resendSchedulingInvitation(family)}
                            disabled={resendingInvite === family.id}
                            data-testid={`resend-invite-${family.id}`}
                            className="rounded-full text-xs"
                          >
                            <Send className="w-3 h-3 mr-1" />
                            {resendingInvite === family.id ? '...' : 'Resend Invite'}
                          </Button>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => viewDetails(family)}
                            data-testid={`view-${family.id}`}
                            className="rounded-full"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                          <Link to={`/families/${family.id}`}>
                            <Button variant="outline" size="sm" className="rounded-full">
                              Details
                            </Button>
                          </Link>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => confirmDelete(family)}
                            data-testid={`delete-${family.id}`}
                            className="rounded-full text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Family Details</DialogTitle>
            <DialogDescription>
              {selectedFamily?.parent_first_name} {selectedFamily?.parent_last_name}
            </DialogDescription>
          </DialogHeader>
          
          {selectedFamily && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-stone-500">Parent Name</label>
                  <p className="text-foreground">{selectedFamily.parent_first_name} {selectedFamily.parent_last_name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-stone-500">Role</label>
                  <p className="text-foreground">{selectedFamily.parent_role || 'Not specified'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-stone-500">Email</label>
                  <p className="text-foreground">{selectedFamily.email}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-stone-500">Phone</label>
                  <p className="text-foreground">{selectedFamily.phone}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-stone-500">Location</label>
                  <p className="text-foreground">{selectedFamily.location_type}: {selectedFamily.location_detail}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-stone-500">Address</label>
                  <p className="text-foreground">{selectedFamily.address}</p>
                </div>
              </div>
              
              {selectedFamily.children && selectedFamily.children.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-stone-500 mb-2 block">Children</label>
                  <div className="space-y-2">
                    {selectedFamily.children.map((child, index) => (
                      <div key={index} className="bg-stone-50 p-3 rounded-lg">
                        <p className="font-medium">{child.name}, Age {child.age}</p>
                        <p className="text-sm text-stone-600">{child.learning_stage} • {child.reading_level}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="sm:max-w-md" data-testid="delete-family-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-700">Delete Family</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this family? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {familyToDelete && (
            <div className="py-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="font-medium text-red-900">
                  {familyToDelete.parent_first_name} {familyToDelete.parent_last_name}
                </p>
                <p className="text-sm text-red-700">{familyToDelete.email}</p>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => { setShowDeleteModal(false); setFamilyToDelete(null); }}
              className="rounded-full"
              data-testid="cancel-delete-button"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting}
              className="rounded-full"
              data-testid="confirm-delete-button"
            >
              {deleting ? 'Deleting...' : 'Delete Family'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule Delivery Modal */}
      <Dialog open={showScheduleModal} onOpenChange={setShowScheduleModal}>
        <DialogContent className="sm:max-w-md" data-testid="schedule-delivery-dialog">
          <DialogHeader>
            <DialogTitle>Schedule Delivery</DialogTitle>
            <DialogDescription>
              Manually schedule a delivery for {familyToSchedule?.parent_first_name} {familyToSchedule?.parent_last_name}. 
              A confirmation email will be sent to the family.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {familyToSchedule && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="font-medium text-blue-900">{familyToSchedule.parent_first_name} {familyToSchedule.parent_last_name}</p>
                <p className="text-sm text-blue-700">{familyToSchedule.email} • {familyToSchedule.phone}</p>
                <p className="text-sm text-blue-700">{familyToSchedule.address}</p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="delivery-date">Delivery Date</Label>
              <Input
                id="delivery-date"
                type="date"
                value={scheduleDate}
                onChange={(e) => setScheduleDate(e.target.value)}
                min={getMinDate()}
                data-testid="schedule-date-input"
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="delivery-time">Delivery Time Slot</Label>
              <Select value={scheduleTime} onValueChange={setScheduleTime}>
                <SelectTrigger data-testid="schedule-time-select" className="h-11">
                  <SelectValue placeholder="Select a time slot" />
                </SelectTrigger>
                <SelectContent>
                  {TIME_SLOTS.map((slot) => (
                    <SelectItem key={slot} value={slot}>{slot}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => { setShowScheduleModal(false); setFamilyToSchedule(null); }}
              className="rounded-full"
              data-testid="cancel-schedule-button"
            >
              Cancel
            </Button>
            <Button
              onClick={handleManualSchedule}
              disabled={scheduling || !scheduleDate || !scheduleTime}
              className="rounded-full"
              data-testid="confirm-schedule-button"
            >
              {scheduling ? 'Scheduling...' : 'Confirm & Send Email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
